import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule,
} from 'vuex-module-decorators';
import store from '@/store';
import { ErrorAlertModule } from '@/store/modules/errors';
import {
  getAddStatementById,
  saveDocuments,
  statementXmlBase64,
  sendPackage,
  AddStatementDraftResp,
  AddStSaveDocuments,
  SendAddPackageRequest,
  deleteStatementLink,
  createAddStDraft,
  CreateAddStDraftPayload,
  saveAddStDraft,
} from '@/api/additionalStatement';
import { CommonObject, IAsyncState } from '@/utils/types';
import { emptyToNull, errorHandler } from '@/utils/common';
import deepmerge from 'deepmerge';
import { DocumentType, StatementType } from '@/types/services';
import { ApplicantRoleConst, FieldName, MrpaType } from '@/constants';
import cloneDeep from 'clone-deep';

@Module({ dynamic: true, store, name: 'AdditionalStatement', namespaced: true })
class AdditionalStatement extends VuexModule {
  public addStatement: IAsyncState<AddStatementDraftResp> = {
    entity: null,
    loading: false,
    loaded: false,
    fetching: false,
  };
  public saveDocumentsState: IAsyncState = {
    loading: false,
    loaded: false,
  };
  public stXmlBase64: IAsyncState<string> = {
    entity: null,
    loading: false,
    loaded: false,
  };
  public sendPackageState: IAsyncState = {
    loading: false,
    loaded: false,
  };
  public deleteStLinkState: IAsyncState = {
    loading: false,
    loaded: false,
  };
  public createDraftState: IAsyncState<AddStatementDraftResp> = {
    entity: null,
    loading: false,
    loaded: false,
  };
  public saveDraftState: IAsyncState = {
    loading: false,
    loaded: false,
  };

  @Mutation
  private pendingGetAddStById() {
    ErrorAlertModule.setErrorAlert('');
    this.addStatement.entity = null;
    this.addStatement.loading = true;
    this.addStatement.fetching = true;
    this.addStatement.loaded = false;
  }

  @Mutation
  private fulfilledGetAddStById(addStatement: AddStatementDraftResp) {
    this.addStatement.entity = addStatement;
    this.addStatement.loading = false;
    this.addStatement.fetching = false;
    this.addStatement.loaded = true;
  }

  @Mutation
  private rejectedGetAddStById() {
    this.addStatement.loading = false;
    this.addStatement.fetching = false;
  }

  @Mutation
  private pendingUpdateAddStById() {
    ErrorAlertModule.setErrorAlert('');
    this.addStatement.fetching = true;
  }

  @Mutation
  private fulfilledUpdateAddStById(addStatement: AddStatementDraftResp) {
    this.addStatement.entity = addStatement;
    this.addStatement.fetching = false;
  }

  @Mutation
  private rejectedUpdateAddStById() {
    this.addStatement.fetching = false;
  }

  @Mutation
  private pendingSaveDocuments() {
    ErrorAlertModule.setErrorAlert('');
    this.saveDocumentsState.loading = true;
    this.saveDocumentsState.loaded = false;
  }

  @Mutation
  private fulfilledSaveDocuments() {
    this.saveDocumentsState.loading = false;
    this.saveDocumentsState.loaded = true;
  }

  @Mutation
  private rejectedSaveDocuments() {
    this.saveDocumentsState.loading = false;
  }

  @Mutation
  private pendingGetStXmlBase64() {
    ErrorAlertModule.setErrorAlert('');
    this.stXmlBase64.entity = null;
    this.stXmlBase64.loading = true;
    this.stXmlBase64.loaded = false;
  }

  @Mutation
  private fulfilledGetStXmlBase64(statementXmlBase64: string) {
    this.stXmlBase64.entity = statementXmlBase64;
    this.stXmlBase64.loading = false;
    this.stXmlBase64.loaded = true;
  }

  @Mutation
  private rejectedGetStXmlBase64(error: unknown) {
    errorHandler(error);
    this.stXmlBase64.loading = false;
  }

  @Mutation
  private pendingSendPackage() {
    ErrorAlertModule.setErrorAlert('');
    this.sendPackageState.loading = true;
    this.sendPackageState.loaded = false;
  }

  @Mutation
  private fulfilledSendPackage() {
    this.sendPackageState.loading = false;
    this.sendPackageState.loaded = true;
  }

  @Mutation
  private rejectedSendPackage(error: unknown) {
    errorHandler(error);
    this.sendPackageState.loading = false;
  }

  @Mutation
  private pendingDeleteStLink() {
    ErrorAlertModule.setErrorAlert('');
    this.deleteStLinkState.loading = true;
    this.deleteStLinkState.loaded = false;
  }

  @Mutation
  private fulfilledDeleteStLink() {
    this.deleteStLinkState.loading = false;
    this.deleteStLinkState.loaded = true;
  }

  @Mutation
  private rejectedDeleteStLink() {
    this.deleteStLinkState.loading = false;
  }

  @Mutation
  private pendingCreateDraft() {
    ErrorAlertModule.setErrorAlert('');
    this.createDraftState.entity = null;
    this.createDraftState.loading = true;
    this.createDraftState.loaded = false;
  }

  @Mutation
  private fulfilledCreateDraft(data: AddStatementDraftResp) {
    this.createDraftState.entity = data;
    this.createDraftState.loading = false;
    this.createDraftState.loaded = true;
  }

  @Mutation
  private rejectedCreateDraft(error: unknown) {
    errorHandler(error);
    this.createDraftState.loading = false;
  }

  @Mutation
  private pendingSaveDraft() {
    ErrorAlertModule.setErrorAlert('');
    this.saveDraftState.loading = true;
    this.saveDraftState.loaded = false;
  }

  @Mutation
  private fulfilledSaveDraft() {
    this.saveDraftState.loading = false;
    this.saveDraftState.loaded = true;
  }

  @Mutation
  private rejectedSaveDraft(error: unknown) {
    errorHandler(error);
    this.saveDraftState.loading = false;
  }

  @Action({ rawError: true })
  async getAddStById(id: string) {
    this.pendingGetAddStById();

    try {
      const statement = await getAddStatementById(id);
      this.fulfilledGetAddStById(statement);
    } catch (error) {
      errorHandler(error);
      this.rejectedGetAddStById();
    }
  }

  @Action({ rawError: true })
  async updateAddStById(id: string) {
    this.pendingUpdateAddStById();

    try {
      const statement = await getAddStatementById(id);
      this.fulfilledUpdateAddStById(statement);
    } catch (error) {
      errorHandler(error);
      this.rejectedUpdateAddStById();

      throw error;
    }
  }

  @Action({ rawError: true })
  async saveDocuments(payload: AddStSaveDocuments) {
    this.pendingSaveDocuments();

    try {
      await saveDocuments(payload);
      this.fulfilledSaveDocuments();
    } catch (error) {
      errorHandler(error);
      this.rejectedSaveDocuments();
    }
  }

  /**
   *  При вызове также происходит генерация xml
   * */
  @Action({ rawError: true })
  async getStXmlBase64(id: string) {
    this.pendingGetStXmlBase64();

    try {
      const statementXmlBase64Response = await statementXmlBase64(id);
      this.fulfilledGetStXmlBase64(statementXmlBase64Response);
    } catch (error) {
      this.rejectedGetStXmlBase64(error);

      throw error;
    }
  }

  @Action({ rawError: true })
  async sendPackage(payload: SendAddPackageRequest) {
    this.pendingSendPackage();

    try {
      await sendPackage(payload);
      this.fulfilledSendPackage();
    } catch (error) {
      this.rejectedSendPackage(error);

      throw error;
    }
  }

  /**
   * @param id - id заявления на предоставление доп. документов
   * */
  @Action({ rawError: true })
  async deleteStLink(id: string) {
    this.pendingDeleteStLink();

    try {
      await deleteStatementLink(id);
      this.fulfilledDeleteStLink();
    } catch (error) {
      errorHandler(error);
      this.rejectedDeleteStLink();
    }
  }

  @Action({ rawError: true })
  async saveDraft({
    initialAddSt,
    trustee,
    role,
  }: {
    initialAddSt: AddStatementDraftResp;
    trustee: CommonObject;
    role: string;
  }) {
    const draft: CommonObject = cloneDeep(initialAddSt);

    if (trustee) {
      const hasAssignee = FieldName.ASSIGNEE_STATUS in trustee;

      if (!trustee.documents.statusDocument?.name) {
        delete trustee.documents.statusDocument;
      }

      const isMrpaByFile =
        trustee.documents.authorityDocument?.type === DocumentType.MRP;
      const isMrpaByGuid = Boolean(trustee.baseAuthority?.mrpIssuerInn);
      const isOldMrpa =
        draft.originalStatementType === StatementType.EXTRACTION_ROD;

      if (isMrpaByFile) {
        if (isOldMrpa) {
          draft.applicantRole = ApplicantRoleConst.TRUSTEE;
        } else {
          draft.applicant.mrpType = MrpaType.BY_FILE;
        }
      } else if (isMrpaByGuid) {
        if (isOldMrpa) {
          draft.applicantRole = ApplicantRoleConst.TRUSTEE;
        } else {
          draft.applicant.mrpType = MrpaType.BY_GUID;
        }
      } else {
        if (isOldMrpa) {
          draft.applicantRole = ApplicantRoleConst.APPLICANT;
        }
      }

      if (isMrpaByFile || isMrpaByGuid) {
        draft.applicant[role].trusteeInfo = trustee;
      } else {
        const trusteeName = hasAssignee ? 'assignee' : 'trusteeInfo';
        const statusName = hasAssignee ? 'status' : 'trusteeStatus';

        draft.applicant[role][trusteeName] = deepmerge(trustee, {
          [statusName]: trustee[statusName],
        });
        if(isOldMrpa){
          draft.applicant[role].trusteeInfo = {
            ...trustee,
            firstName: trustee.firstName || trustee.name,
            name: undefined
          };
        }
      }

      if (hasAssignee) {
        draft.applicant[role].trusteeInfo = null;
      } else {
        draft.applicant[role].assignee = null;
      }
    }

    this.pendingSaveDraft();

    try {
      const addStatement = emptyToNull(draft);
      await saveAddStDraft(addStatement);
      this.fulfilledSaveDraft();

      return addStatement;
    } catch (error) {
      this.rejectedSaveDraft(error);

      throw error;
    }
  }

  @Action({ rawError: true })
  public async createDraft(request: CreateAddStDraftPayload) {
    this.pendingCreateDraft();

    try {
      const data = await createAddStDraft(request);
      this.fulfilledCreateDraft(data);
    } catch (error) {
      this.rejectedCreateDraft(error);

      throw error;
    }
  }
}

export const AddStModule = getModule(AdditionalStatement);
